import React from 'react';
import { connect } from 'react-redux';
import { selectViewType, toggleMobileSideNav, VIEW_TYPE } from 'src/app/slice';
import {
  SelectedToolbarItem,
  selectSelectedToolbarItem,
  selectToolbarItems,
} from 'src/features/nav/store/slice';
import { RootState } from 'src/app/store';
import { DesktopBreadcrumbs } from '../BreadcrumbIA2/DesktopBreadcrumbs';
import {
  ContextBarWrapper,
  MobileMenuButton,
  MobileMenuButtonIcon,
} from './styles';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { ConnectedAppNameSection } from './AppNameSection';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import {
  isStandardPageLayoutType,
  shouldRenderMenu,
} from 'src/commons/utils/helpers';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

interface ContextBarProps {
  viewType: VIEW_TYPE;
  hasToolbarItems: boolean;
  selectedToolbarItem: SelectedToolbarItem | undefined;
  mobileMenuBtnClickHandler: (...args: any[]) => any;
}

export const ContextBar: React.VFC<ContextBarProps> = ({
  viewType,
  hasToolbarItems,
  selectedToolbarItem,
  mobileMenuBtnClickHandler,
}) => {
  const bundle = useSidenavBundle();
  const chromeConfig = useChromeConfigContext();
  const isStandardLayout = isStandardPageLayoutType(
    chromeConfig.pageLayoutType,
  );
  const breadcrumbs = chromeConfig.breadcrumbs || [];
  const appName = chromeConfig.appName;
  const shouldRenderAppName =
    isStandardLayout && (!!selectedToolbarItem || !!appName);
  const shouldRenderMobileMenu =
    viewType === VIEW_TYPE.MOBILE &&
    shouldRenderMenu(viewType, chromeConfig.pageLayoutType) &&
    hasToolbarItems;

  const hasPageLabel = () => {
    const pageTitleLabel = chromeConfig.pageTitleBreadcrumb?.label;
    const pageTitleObjectType = chromeConfig.pageTitleBreadcrumb?.objectType;
    return pageTitleLabel || pageTitleObjectType;
  };
  const shouldRenderBreadcrumbs =
    viewType === VIEW_TYPE.DESKTOP &&
    isStandardLayout &&
    (breadcrumbs?.length > 0 || !!hasPageLabel());

  return (
    <ContextBarWrapper data-ccx-e2e-id="aac-context-bar">
      {shouldRenderMobileMenu && (
        <MobileMenuButton
          onClick={mobileMenuBtnClickHandler}
          aria-label={bundle.getMessage('sidenav-open-menu-aria-label')}
          data-ccx-e2e-id="open-menu-mobile-btn"
          {...getTaktAttributes(
            'open-menu-mobile-btn',
            chromeComponents.HEADER,
          )}
        >
          <MobileMenuButtonIcon
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M.643 2.755h16.714c.355 0 .643-.274.643-.612V.613C18 .273 17.712 0 17.357 0H.643C.288 0 0 .274 0 .612v1.53c0 .339.288.613.643.613zm0 6.123h16.714c.355 0 .643-.275.643-.613v-1.53c0-.338-.288-.613-.643-.613H.643c-.355 0-.643.275-.643.613v1.53c0 .338.288.613.643.613zm0 6.122h16.714c.355 0 .643-.274.643-.612v-1.53c0-.339-.288-.613-.643-.613H.643c-.355 0-.643.274-.643.612v1.53c0 .339.288.613.643.613z" />
          </MobileMenuButtonIcon>
        </MobileMenuButton>
      )}
      {shouldRenderAppName && (
        <ConnectedAppNameSection
          chromeConfigAppName={appName}
          isMobile={shouldRenderMobileMenu}
        />
      )}
      {shouldRenderBreadcrumbs && (
        <DesktopBreadcrumbs shouldRenderAppName={shouldRenderAppName} />
      )}
    </ContextBarWrapper>
  );
};

const connector = connect(
  (state: RootState) => {
    const viewType = selectViewType(state);
    const selectedToolbarItem = selectSelectedToolbarItem(state);
    const hasToolbarItems = selectToolbarItems(state).length > 0;
    return {
      viewType,
      hasToolbarItems,
      selectedToolbarItem,
    };
  },
  (dispatch) => {
    return {
      mobileMenuBtnClickHandler: () => dispatch(toggleMobileSideNav()),
    };
  },
);

export const ConnectedContextBar = connector(ContextBar);
